import { useEffect, useRef, useState } from "react";
import { getImageDownloadURL } from "@/utils/entryUtils";
import { Loader2 } from "lucide-react";
import { Timestamp } from "firebase/firestore";

interface StorageImageProps {
  storagePath: string;
  uploadStatus?: string;
  updatedAt?: Timestamp;
  alt?: string;
  className?: string;
}

/**
 * Component that displays an image from Firebase Storage
 * Converts storage path to download URL
 * Re-fetches when updatedAt changes to handle re-uploads correctly
 */
export const StorageImage: React.FC<StorageImageProps> = ({
  storagePath,
  uploadStatus,
  updatedAt,
  alt = "Image",
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (shouldLoad) return;

    const element = containerRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px" }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return;

    let isMounted = true;

    const loadImage = async () => {
      setLoading(true);
      setError(false);

      try {
        const url = await getImageDownloadURL(storagePath, uploadStatus, updatedAt);
        if (isMounted) {
          if (url) {
            setImageUrl(url);
          } else {
            setError(true);
          }
        }
      } catch (err) {
        console.error("Error loading image:", err);
        if (isMounted) {
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (storagePath) {
      loadImage();
    } else {
      setLoading(false);
      setError(true);
    }

    return () => {
      isMounted = false;
    };
  }, [shouldLoad, storagePath, uploadStatus, updatedAt]);

  if (!shouldLoad) {
    return <div ref={containerRef} className={`${className} bg-gray-100`} />;
  }

  if (loading) {
    return (
      <div ref={containerRef} className={`${className} bg-gray-100 flex items-center justify-center`}>
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div ref={containerRef} className={`${className} bg-gray-100 flex items-center justify-center`}>
        <span className="text-xs text-gray-400">No image</span>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
    />
  );
};
